import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { playAlertSound, stopAlertSound } from '../utils/alertSounds';

/** Configurable membership tiers in admin Settings (starter + premium). Alpha is admin-assigned only. */
const MEMBERSHIP_TIER_UI = [
  { id: 'starter', label: 'Starter', emoji: '🌱', cardClass: 'bg-gray-50 dark:bg-gray-800/50', showPrice: false },
  { id: 'premium', label: 'Premium', emoji: '⭐', cardClass: 'bg-yellow-50 dark:bg-yellow-900/20', showPrice: true, hideLimits: true },
];

function normalizeTierSettings(data) {
  if (!data) return data;
  const next = { ...data };
  if (next.userTiers?.free) {
    next.userTiers = {
      ...next.userTiers,
      starter: { ...next.userTiers.starter, ...next.userTiers.free },
    };
    const { free, ...rest } = next.userTiers;
    next.userTiers = rest;
  }
  if (next.groupTiers?.free) {
    next.groupTiers = {
      ...next.groupTiers,
      starter: { ...next.groupTiers.starter, ...next.groupTiers.free },
    };
    const { free, ...rest } = next.groupTiers;
    next.groupTiers = rest;
  }
  return next;
}

function tierField(map, tierId, field, fallback = '') {
  return map?.[tierId]?.[field] ?? map?.free?.[field] ?? fallback;
}

// Helper to get sound settings from localStorage (with defaults)
const getSoundSettings = () => {
  try {
    const saved = localStorage.getItem('akb_sound_settings');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error reading sound settings:', e);
  }
  // Default: enabled with 80% volume
  return { soundEnabled: true, alertVolume: 0.8 };
};

// Helper to save sound settings to localStorage
const saveSoundSettings = (soundEnabled, alertVolume) => {
  try {
    localStorage.setItem('akb_sound_settings', JSON.stringify({ soundEnabled, alertVolume }));
  } catch (e) {
    console.error('Error saving sound settings:', e);
  }
};

export default function Settings() {
  const { admin, refreshAdmin } = useAuth();

  // Get sound settings from localStorage on initial load
  const initialSoundSettings = getSoundSettings();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [playingSound, setPlayingSound] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(initialSoundSettings.soundEnabled);
  const [alertVolume, setAlertVolume] = useState(initialSoundSettings.alertVolume);
  const audioRef = useRef(null);

  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorStep, setTwoFactorStep] = useState('idle'); // idle, setup, verify, disable
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');

  // IP Whitelist State
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [currentIP, setCurrentIP] = useState('');
  const [newIPAddress, setNewIPAddress] = useState('');
  const [newIPLabel, setNewIPLabel] = useState('');
  const [ipLoading, setIpLoading] = useState(false);
  const [ipError, setIpError] = useState('');
  const [ipSuccess, setIpSuccess] = useState('');

  // Account Settings State
  const [accountEmail, setAccountEmail] = useState('');
  const [accountUsername, setAccountUsername] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');

  // System Monitor State
  const [systemProcesses, setSystemProcesses] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemError, setSystemError] = useState('');
  const [selectedProcessLogs, setSelectedProcessLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsType, setLogsType] = useState('out');
  const [gitInfo, setGitInfo] = useState(null);
  const [gitLoading, setGitLoading] = useState(false);
  const [apiHealth, setApiHealth] = useState(null);
  const [apiHealthLoading, setApiHealthLoading] = useState(false);

  // Cron Job Management State
  const [cronJobs, setCronJobs] = useState([]);
  const [cronLoading, setCronLoading] = useState(false);
  const [showCronForm, setShowCronForm] = useState(false);
  const [editingCron, setEditingCron] = useState(null); // null = add new, index = editing
  const [cronForm, setCronForm] = useState({ schedule: '', command: '', name: '' });
  const [cronRunning, setCronRunning] = useState(null); // id of currently running job
  const [cronOutput, setCronOutput] = useState(null);
  const [cronLog, setCronLog] = useState(null);
  const [cronLogLoading, setCronLogLoading] = useState(false);

  // Muted Chats add form
  const [mutedChatIdInput, setMutedChatIdInput] = useState('');
  const [mutedChatLabelInput, setMutedChatLabelInput] = useState('');
  const [mutedChatTypeInput, setMutedChatTypeInput] = useState('group');

  useEffect(() => {
    fetchSettings();
    fetch2FAStatus();
    fetchIPWhitelist();
  }, []);

  // Save sound settings to localStorage whenever they change
  useEffect(() => {
    saveSoundSettings(soundEnabled, alertVolume);
  }, [soundEnabled, alertVolume]);

  // Initialize account fields from admin data
  useEffect(() => {
    if (admin) {
      setAccountEmail(admin.email || '');
      setAccountUsername(admin.username || '');
    }
  }, [admin]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(normalizeTierSettings(response.data));
      setError(null);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section = null) => {
    setSaving(true);
    setSuccess(false);
    try {
      if (section) {
        // Save specific section
        await api.put(`/settings/${section}`, settings[section]);
      } else {
        // Save all settings
        await api.put('/settings', settings);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults?')) return;
    try {
      await api.post('/settings/reset');
      await fetchSettings();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to reset settings:', error);
      alert('Failed to reset settings');
    }
  };

  const updateSetting = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const handleChainToggle = (chain) => {
    const currentChains = settings.blockchain?.supportedChains || [];
    const newChains = currentChains.includes(chain)
      ? currentChains.filter(c => c !== chain)
      : [...currentChains, chain];
    updateSetting('blockchain.supportedChains', newChains);
  };

  const playTestSound = async (soundType) => {
    try {
      setPlayingSound(soundType);
      if (audioRef.current) {
        clearTimeout(audioRef.current);
        audioRef.current = null;
      }
      stopAlertSound();
      await playAlertSound(soundType, alertVolume);
      setPlayingSound(null);
    } catch (err) {
      console.error('Error playing sound:', err);
      setPlayingSound(null);
      alert('Failed to play sound. Please check your browser settings.');
    }
  };

  const stopSound = () => {
    stopAlertSound();
    if (audioRef.current) {
      clearTimeout(audioRef.current);
      audioRef.current = null;
    }
    setPlayingSound(null);
  };

  // 2FA Functions
  const fetch2FAStatus = async () => {
    try {
      const response = await api.get('/auth/2fa/status');
      setTwoFactorEnabled(response.data.enabled);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  const start2FASetup = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError('');
    setTwoFactorSuccess('');
    try {
      const response = await api.post('/auth/2fa/setup');
      setTwoFactorSetup(response.data);
      setTwoFactorStep('setup');
    } catch (err) {
      setTwoFactorError(err.response?.data?.error || 'Failed to start 2FA setup');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const verify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setTwoFactorError('Please enter a 6-digit code');
      return;
    }
    setTwoFactorLoading(true);
    setTwoFactorError('');
    try {
      await api.post('/auth/2fa/verify', { token: verifyCode });
      setTwoFactorEnabled(true);
      setTwoFactorStep('idle');
      setTwoFactorSetup(null);
      setVerifyCode('');
      setTwoFactorSuccess('2FA enabled successfully!');
      setTimeout(() => setTwoFactorSuccess(''), 3000);
    } catch (err) {
      setTwoFactorError(err.response?.data?.error || 'Failed to verify code');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePassword) {
      setTwoFactorError('Please enter your password');
      return;
    }
    if (twoFactorEnabled && (!disableCode || disableCode.length !== 6)) {
      setTwoFactorError('Please enter your current 2FA code');
      return;
    }
    setTwoFactorLoading(true);
    setTwoFactorError('');
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword, token: disableCode });
      setTwoFactorEnabled(false);
      setTwoFactorStep('idle');
      setDisablePassword('');
      setDisableCode('');
      setTwoFactorSuccess('2FA disabled successfully!');
      setTimeout(() => setTwoFactorSuccess(''), 3000);
    } catch (err) {
      setTwoFactorError(err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const cancel2FAAction = () => {
    setTwoFactorStep('idle');
    setTwoFactorSetup(null);
    setVerifyCode('');
    setDisablePassword('');
    setDisableCode('');
    setTwoFactorError('');
  };

  // IP Whitelist Functions
  const fetchIPWhitelist = async () => {
    try {
      const response = await api.get('/settings/ip-whitelist');
      setIpWhitelist(response.data.ipWhitelist || []);
      setCurrentIP(response.data.currentIP || '');
    } catch (err) {
      console.error('Failed to fetch IP whitelist:', err);
    }
  };

  const addIPToWhitelist = async () => {
    if (!newIPAddress.trim()) {
      setIpError('IP address is required');
      return;
    }
    setIpLoading(true);
    setIpError('');
    setIpSuccess('');
    try {
      await api.post('/settings/ip-whitelist', {
        address: newIPAddress.trim(),
        label: newIPLabel.trim()
      });
      setNewIPAddress('');
      setNewIPLabel('');
      setIpSuccess('IP address added successfully');
      setTimeout(() => setIpSuccess(''), 3000);
      fetchIPWhitelist();
    } catch (err) {
      setIpError(err.response?.data?.error || 'Failed to add IP address');
    } finally {
      setIpLoading(false);
    }
  };

  const toggleIPEnabled = async (id, enabled) => {
    try {
      await api.put(`/settings/ip-whitelist/${id}`, { enabled: !enabled });
      fetchIPWhitelist();
    } catch (err) {
      setIpError(err.response?.data?.error || 'Failed to update IP');
    }
  };

  const removeIPFromWhitelist = async (id) => {
    if (!window.confirm('Are you sure you want to remove this IP from the whitelist?')) return;
    try {
      await api.delete(`/settings/ip-whitelist/${id}`);
      setIpSuccess('IP address removed');
      setTimeout(() => setIpSuccess(''), 3000);
      fetchIPWhitelist();
    } catch (err) {
      setIpError(err.response?.data?.error || 'Failed to remove IP');
    }
  };

  const addCurrentIP = () => {
    setNewIPAddress(currentIP);
    setNewIPLabel('My IP');
  };

  // Account Functions
  const updateProfile = async () => {
    if (!accountPassword) {
      setAccountError('Please enter your current password to confirm changes');
      return;
    }

    setAccountLoading(true);
    setAccountError('');
    setAccountSuccess('');

    try {
      const response = await api.put('/auth/profile', {
        email: accountEmail,
        username: accountUsername,
        currentPassword: accountPassword
      });

      setAccountSuccess('Profile updated successfully');
      setAccountPassword('');
      setTimeout(() => setAccountSuccess(''), 3000);

      // Refresh admin data in context
      if (refreshAdmin) {
        refreshAdmin();
      }
    } catch (err) {
      setAccountError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setAccountLoading(false);
    }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setAccountError('All password fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setAccountError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setAccountError('New password must be at least 6 characters');
      return;
    }

    setAccountLoading(true);
    setAccountError('');
    setAccountSuccess('');

    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });

      setAccountSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setAccountSuccess(''), 3000);
    } catch (err) {
      setAccountError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setAccountLoading(false);
    }
  };

  // System Monitor Functions
  const fetchSystemProcesses = async () => {
    setSystemLoading(true);
    setSystemError('');
    try {
      const response = await api.get('/settings/system/processes');
      setSystemProcesses(response.data);
    } catch (err) {
      setSystemError(err.response?.data?.error || 'Failed to fetch system processes');
    } finally {
      setSystemLoading(false);
    }
  };

  const pm2Action = async (action, processName) => {
    if (!window.confirm(`Are you sure you want to ${action} "${processName}"?`)) return;
    try {
      await api.post(`/settings/system/pm2/${action}`, { processName });
      // Refresh processes after action
      setTimeout(fetchSystemProcesses, 1000);
    } catch (err) {
      alert(`Failed to ${action} process: ${err.response?.data?.error || err.message}`);
    }
  };

  const fetchProcessLogs = async (processName, type = 'out') => {
    setLogsLoading(true);
    setLogsType(type);
    try {
      const response = await api.get(`/settings/system/logs/${processName}?type=${type}&lines=100`);
      setSelectedProcessLogs(response.data);
    } catch (err) {
      alert(`Failed to fetch logs: ${err.response?.data?.error || err.message}`);
    } finally {
      setLogsLoading(false);
    }
  };

  // Cron Job Management Functions
  const fetchCronJobs = async () => {
    setCronLoading(true);
    try {
      const response = await api.get('/settings/system/cron');
      setCronJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch cron jobs:', err);
    } finally {
      setCronLoading(false);
    }
  };

  const saveCronJob = async () => {
    if (!cronForm.schedule || !cronForm.command) {
      alert('Schedule and command are required');
      return;
    }
    try {
      if (editingCron !== null) {
        await api.put(`/settings/system/cron/${editingCron}`, cronForm);
      } else {
        await api.post('/settings/system/cron', cronForm);
      }
      setShowCronForm(false);
      setEditingCron(null);
      setCronForm({ schedule: '', command: '', name: '' });
      fetchCronJobs();
      setTimeout(fetchSystemProcesses, 500);
    } catch (err) {
      alert(`Failed to save cron job: ${err.response?.data?.error || err.message}`);
    }
  };

  const deleteCronJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cron job?')) return;
    try {
      await api.delete(`/settings/system/cron/${id}`);
      fetchCronJobs();
      setTimeout(fetchSystemProcesses, 500);
    } catch (err) {
      alert(`Failed to delete cron job: ${err.response?.data?.error || err.message}`);
    }
  };

  const runCronJob = async (id) => {
    setCronRunning(id);
    setCronOutput(null);
    try {
      const response = await api.post(`/settings/system/cron/${id}/run`);
      setCronOutput({ jobId: id, ...response.data });
    } catch (err) {
      setCronOutput({
        jobId: id,
        success: false,
        error: err.response?.data?.error || err.message,
        output: err.response?.data?.output || '',
        errors: err.response?.data?.errors || '',
      });
    } finally {
      setCronRunning(null);
    }
  };

  const fetchCronLog = async () => {
    setCronLogLoading(true);
    try {
      const response = await api.get('/settings/system/cron/log?lines=80');
      setCronLog(response.data.log);
    } catch (err) {
      setCronLog('Failed to load log');
    } finally {
      setCronLogLoading(false);
    }
  };

  const openEditCron = (job) => {
    setEditingCron(job.id);
    setCronForm({ schedule: job.schedule, command: job.command, name: job.name || '' });
    setShowCronForm(true);
  };

  const openAddCron = () => {
    setEditingCron(null);
    setCronForm({ schedule: '0 3 * * *', command: 'cd /root/CKB && node ', name: '' });
    setShowCronForm(true);
  };

  const CRON_PRESETS = [
    { label: 'Every day at 3 AM', value: '0 3 * * *' },
    { label: 'Every 6 hours', value: '0 */6 * * *' },
    { label: 'Every 12 hours', value: '0 */12 * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every 30 minutes', value: '*/30 * * * *' },
    { label: 'Every Sunday at midnight', value: '0 0 * * 0' },
  ];

  const formatUptime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const uptime = Date.now() - timestamp;
    const days = Math.floor(uptime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((uptime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((uptime % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const fetchGitInfo = async () => {
    setGitLoading(true);
    try {
      const response = await api.get('/settings/system/git');
      setGitInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch git info:', err);
    } finally {
      setGitLoading(false);
    }
  };

  const fetchApiHealth = async () => {
    setApiHealthLoading(true);
    try {
      const response = await api.get('/settings/system/api-health');
      setApiHealth(response.data);
    } catch (err) {
      console.error('Failed to fetch API health:', err);
    } finally {
      setApiHealthLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading settings...</div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  // Grouped sidebar menu structure
  const menuGroups = [
    {
      name: 'User',
      items: [
        { id: 'account', name: 'Account', icon: '👤' },
        { id: 'security', name: 'Security', icon: '🔐' },
      ]
    },
    {
      name: 'Alerts',
      items: [
        { id: 'alertSettings', name: 'Alert Settings', icon: '🔔' },
        { id: 'adminGroup', name: 'Admin Group', icon: '👑' },
        { id: 'mutedChats', name: 'Muted Chats', icon: '🔇' },
        { id: 'templates', name: 'Templates', icon: '📝' },
      ]
    },
    {
      name: 'Bot Config',
      items: [
        { id: 'general', name: 'General', icon: '⚙️' },
        { id: 'tiers', name: 'Membership Tiers', icon: '💎' },
        { id: 'walletMonitor', name: 'Wallet Monitor', icon: '👛' },
      ]
    },
    {
      name: 'System',
      items: [
        { id: 'features', name: 'Feature Flags', icon: '🚩' },
        { id: 'cleanup', name: 'Data Cleanup', icon: '🧹' },
        { id: 'processor', name: 'System Monitor', icon: '🖥️' },
      ]
    }
  ];

  // Flatten tabs for backwards compatibility
  const tabs = menuGroups.flatMap(g => g.items);

  return (
    <div className="flex gap-6">
      {/* Sidebar Navigation */}
      <div className="w-56 flex-shrink-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900 dark:text-white">Settings</h2>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className={`px-3 py-1 text-xs rounded font-medium ${
                saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {saving ? '...' : 'Save'}
            </button>
          </div>

          {menuGroups.map((group, groupIdx) => (
            <div key={group.name} className={groupIdx > 0 ? 'mt-4 pt-4 border-t' : ''}>
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{group.name}</h3>
              <div className="space-y-1">
                {group.items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      activeTab === item.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span>{item.icon}</span>
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-4 pt-4 border-t">
            <button
              onClick={handleReset}
              className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <span>↻</span>
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
            <p className="text-green-800">Settings saved successfully!</p>
          </div>
        )}

        <div className="max-w-3xl">
        {/* Account Settings */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            {/* Profile Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">👤 Profile Information</h2>

              {accountSuccess && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <p className="text-green-800 text-sm">{accountSuccess}</p>
                </div>
              )}

              {accountError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-red-800 text-sm">{accountError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Username</label>
                  <input
                    type="text"
                    value={accountUsername}
                    onChange={(e) => setAccountUsername(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Email</label>
                  <input
                    type="email"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Current Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter current password to confirm changes"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Required to save profile changes</p>
                </div>
                <div className="pt-2">
                  <button
                    onClick={updateProfile}
                    disabled={accountLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {accountLoading ? 'Saving...' : 'Update Profile'}
                  </button>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🔑 Change Password</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="pt-2">
                  <button
                    onClick={changePassword}
                    disabled={accountLoading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {accountLoading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">ℹ️ Account Information</h2>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Role</span>
                  <span className="font-medium capitalize">{admin?.role?.replace('_', ' ') || 'Admin'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Account Status</span>
                  <span className={`font-medium ${admin?.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {admin?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Last Login</span>
                  <span className="font-medium">
                    {admin?.lastLogin ? new Date(admin.lastLogin).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Account Created</span>
                  <span className="font-medium">
                    {admin?.createdAt ? new Date(admin.createdAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* General Settings */}
        {activeTab === 'general' && settings && (
          <div className="space-y-6">
            {/* Deposit Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">💳 Deposit Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Minimum Deposit (Credits)
                  </label>
                  <input
                    type="number"
                    value={settings.deposit?.minCreditDeposit || 10}
                    onChange={(e) => updateSetting('deposit.minCreditDeposit', parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Deposit Expiry (Hours)
                  </label>
                  <input
                    type="number"
                    value={settings.deposit?.depositExpiryHours || 24}
                    onChange={(e) => updateSetting('deposit.depositExpiryHours', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Credit Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.deposit?.creditPrice || 1.0}
                    onChange={(e) => updateSetting('deposit.creditPrice', parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    checked={settings.deposit?.autoStartPaymentMonitor ?? true}
                    onChange={(e) => updateSetting('deposit.autoStartPaymentMonitor', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Auto-start payment monitor on boot
                  </label>
                </div>
              </div>
            </div>

            {/* Blockchain Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">⛓️ Blockchain Settings</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Supported Chains</label>
                <div className="flex flex-wrap gap-3">
                  {['solana', 'base', 'ethereum', 'bsc'].map((chain) => (
                    <label key={chain} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.blockchain?.supportedChains?.includes(chain) ?? true}
                        onChange={() => handleChainToggle(chain)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">{chain}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Solana RPC URL</label>
                  <input
                    type="text"
                    value={settings.blockchain?.solanaRpcUrl || ''}
                    onChange={(e) => updateSetting('blockchain.solanaRpcUrl', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Base RPC URL</label>
                  <input
                    type="text"
                    value={settings.blockchain?.baseRpcUrl || ''}
                    onChange={(e) => updateSetting('blockchain.baseRpcUrl', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Ethereum RPC URL</label>
                  <input
                    type="text"
                    value={settings.blockchain?.ethereumRpcUrl || ''}
                    onChange={(e) => updateSetting('blockchain.ethereumRpcUrl', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">BSC RPC URL</label>
                  <input
                    type="text"
                    value={settings.blockchain?.bscRpcUrl || ''}
                    onChange={(e) => updateSetting('blockchain.bscRpcUrl', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Wallet Addresses */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🏦 Main Wallet Addresses</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">These are the wallets where deposits will be swept to.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">EVM Wallet (Base, ETH, BSC)</label>
                  <input
                    type="text"
                    value={settings.wallets?.mainWalletEvm || ''}
                    onChange={(e) => updateSetting('wallets.mainWalletEvm', e.target.value)}
                    placeholder="0x..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Solana Wallet</label>
                  <input
                    type="text"
                    value={settings.wallets?.mainWalletSolana || ''}
                    onChange={(e) => updateSetting('wallets.mainWalletSolana', e.target.value)}
                    placeholder="Solana address..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            {/* System Limits */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🚦 System Limits</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Commands Per Minute</label>
                  <input
                    type="number"
                    value={settings.limits?.commandsPerMinute || 10}
                    onChange={(e) => updateSetting('limits.commandsPerMinute', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Max Groups Per User</label>
                  <input
                    type="number"
                    value={settings.limits?.maxGroupsPerUser || 50}
                    onChange={(e) => updateSetting('limits.maxGroupsPerUser', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Autoban After Violations</label>
                  <input
                    type="number"
                    value={settings.limits?.autobanAfterViolations || 5}
                    onChange={(e) => updateSetting('limits.autobanAfterViolations', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Violation Reset (Days)</label>
                  <input
                    type="number"
                    value={settings.limits?.violationResetDays || 30}
                    onChange={(e) => updateSetting('limits.violationResetDays', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alert Settings */}
        {activeTab === 'alertSettings' && settings && (
          <div className="space-y-6">
            {/* Private Chat Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-blue-200 dark:border-blue-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">💬 Private Chat Settings</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Configure alerts for direct user-to-bot conversations (DMs).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Milestone Settings */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <h3 className="font-semibold text-blue-900 mb-3">🎯 Milestone Settings</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable Milestone Alerts</span>
                      <button
                        onClick={() => updateSetting('privateChatSettings.enableMilestoneAlerts', !settings.privateChatSettings?.enableMilestoneAlerts)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          settings.privateChatSettings?.enableMilestoneAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                          settings.privateChatSettings?.enableMilestoneAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Milestones (x multiplier)</label>
                      <input
                        type="text"
                        value={(settings.privateChatSettings?.milestones || [2, 5, 10, 20, 50, 100]).join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                          updateSetting('privateChatSettings.milestones', values);
                        }}
                        className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="2, 5, 10, 20, 50, 100"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Comma-separated multipliers</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Cooldown (minutes)</label>
                      <input
                        type="number"
                        value={settings.privateChatSettings?.milestoneCooldownMinutes || 60}
                        onChange={(e) => updateSetting('privateChatSettings.milestoneCooldownMinutes', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Recovery Settings */}
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                  <h3 className="font-semibold text-green-900 mb-3">📈 Recovery Settings</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable Recovery Alerts</span>
                      <button
                        onClick={() => updateSetting('privateChatSettings.enableRecoveryAlerts', !settings.privateChatSettings?.enableRecoveryAlerts)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          settings.privateChatSettings?.enableRecoveryAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                          settings.privateChatSettings?.enableRecoveryAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Drawdown Threshold (%)</label>
                      <input
                        type="number"
                        value={settings.privateChatSettings?.minDrawdownPercent || 70}
                        onChange={(e) => updateSetting('privateChatSettings.minDrawdownPercent', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="10"
                        max="99"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Min drop from ATH to start tracking recovery</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Min Recovery % to Alert</label>
                      <input
                        type="number"
                        value={settings.privateChatSettings?.minRecoveryPercent || 50}
                        onChange={(e) => updateSetting('privateChatSettings.minRecoveryPercent', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Only alert when recovery ≥ this %</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Min market cap ($)</label>
                      <input
                        type="number"
                        value={settings.privateChatSettings?.minMarketCapForRecovery ?? settings.recovery?.minMarketCapForRecovery ?? 5000}
                        onChange={(e) => updateSetting('privateChatSettings.minMarketCapForRecovery', parseInt(e.target.value, 10))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="0"
                        step="1000"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No recovery tracking or alerts below this MC (default $5,000)</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Recovery Milestones (%)</label>
                      <input
                        type="text"
                        value={(settings.privateChatSettings?.recoveryMilestones || [50, 100, 200, 300, 500, 1000]).join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                          updateSetting('privateChatSettings.recoveryMilestones', values);
                        }}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        placeholder="50, 100, 200, 300, 500, 1000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Cooldown (minutes)</label>
                      <input
                        type="number"
                        value={settings.privateChatSettings?.recoveryCooldownMinutes || 30}
                        onChange={(e) => updateSetting('privateChatSettings.recoveryCooldownMinutes', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* General Group Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-green-200 dark:border-green-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">👥 General Group Settings</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Configure alerts for ALL groups that add the bot (public and private groups).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Milestone Settings */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <h3 className="font-semibold text-blue-900 mb-3">🎯 Milestone Settings</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable Milestone Alerts</span>
                      <button
                        onClick={() => updateSetting('generalGroupSettings.enableMilestoneAlerts', !settings.generalGroupSettings?.enableMilestoneAlerts)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          settings.generalGroupSettings?.enableMilestoneAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                          settings.generalGroupSettings?.enableMilestoneAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Milestones (x multiplier)</label>
                      <input
                        type="text"
                        value={(settings.generalGroupSettings?.milestones || [10, 20, 50, 100]).join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                          updateSetting('generalGroupSettings.milestones', values);
                        }}
                        className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="10, 20, 50, 100"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Default: 10, 20, 50, 100 (no 5x)</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Cooldown (minutes)</label>
                      <input
                        type="number"
                        value={settings.generalGroupSettings?.milestoneCooldownMinutes || 60}
                        onChange={(e) => updateSetting('generalGroupSettings.milestoneCooldownMinutes', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Recovery Settings */}
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                  <h3 className="font-semibold text-green-900 mb-3">📈 Recovery Settings</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable Recovery Alerts</span>
                      <button
                        onClick={() => updateSetting('generalGroupSettings.enableRecoveryAlerts', !settings.generalGroupSettings?.enableRecoveryAlerts)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          settings.generalGroupSettings?.enableRecoveryAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                          settings.generalGroupSettings?.enableRecoveryAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Drawdown Threshold (%)</label>
                      <input
                        type="number"
                        value={settings.generalGroupSettings?.minDrawdownPercent || 70}
                        onChange={(e) => updateSetting('generalGroupSettings.minDrawdownPercent', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="10"
                        max="99"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Min drop from ATH to start tracking recovery</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Min Recovery % to Alert</label>
                      <input
                        type="number"
                        value={settings.generalGroupSettings?.minRecoveryPercent || 150}
                        onChange={(e) => updateSetting('generalGroupSettings.minRecoveryPercent', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Only alert when recovery ≥ this % (default: 150%)</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Min market cap ($)</label>
                      <input
                        type="number"
                        value={settings.generalGroupSettings?.minMarketCapForRecovery ?? settings.recovery?.minMarketCapForRecovery ?? 5000}
                        onChange={(e) => updateSetting('generalGroupSettings.minMarketCapForRecovery', parseInt(e.target.value, 10))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="0"
                        step="1000"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No recovery tracking or alerts below this MC (default $5,000)</p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Recovery Milestones (%)</label>
                      <input
                        type="text"
                        value={(settings.generalGroupSettings?.recoveryMilestones || [150, 200, 300, 500, 1000]).join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                          updateSetting('generalGroupSettings.recoveryMilestones', values);
                        }}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        placeholder="150, 200, 300, 500, 1000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Cooldown (minutes)</label>
                      <input
                        type="number"
                        value={settings.generalGroupSettings?.recoveryCooldownMinutes || 60}
                        onChange={(e) => updateSetting('generalGroupSettings.recoveryCooldownMinutes', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong>Note:</strong> These settings control alerts sent to the original chat where tokens are tracked.
                  Admin group alerts are configured separately in the Admin Group section.
                </p>
              </div>
            </div>

            {/* Dashboard Sound Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">🔊 Dashboard Sound Settings</h2>
                <span className="text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">Auto-saved locally</span>
              </div>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Enable Alert Sounds</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Play sounds for milestone, recovery, and new-token alerts</p>
                  </div>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      soundEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                      soundEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Volume: {Math.round(alertVolume * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={alertVolume}
                    onChange={(e) => setAlertVolume(parseFloat(e.target.value))}
                    className="w-full"
                    disabled={!soundEnabled}
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Test Sounds:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <button
                      onClick={() => playTestSound('milestone')}
                      disabled={!soundEnabled || playingSound === 'milestone'}
                      className={`px-4 py-3 rounded-lg font-medium transition-all text-sm ${
                        playingSound === 'milestone'
                          ? 'bg-yellow-200 animate-pulse'
                          : soundEnabled
                            ? 'bg-yellow-50 hover:bg-yellow-100 border border-yellow-300'
                            : 'bg-gray-100 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      🎯 Milestone
                    </button>
                    <button
                      onClick={() => playTestSound('recoveryDetected')}
                      disabled={!soundEnabled || playingSound === 'recoveryDetected'}
                      className={`px-4 py-3 rounded-lg font-medium transition-all text-sm ${
                        playingSound === 'recoveryDetected'
                          ? 'bg-blue-200 animate-pulse'
                          : soundEnabled
                            ? 'bg-blue-50 hover:bg-blue-100 border border-blue-300'
                            : 'bg-gray-100 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      📈 Recovery Detected
                    </button>
                    <button
                      onClick={() => playTestSound('recoveryAlert')}
                      disabled={!soundEnabled || playingSound === 'recoveryAlert'}
                      className={`px-4 py-3 rounded-lg font-medium transition-all text-sm ${
                        playingSound === 'recoveryAlert'
                          ? 'bg-green-200 animate-pulse'
                          : soundEnabled
                            ? 'bg-green-50 hover:bg-green-100 border border-green-300'
                            : 'bg-gray-100 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      🎯 Recovery Alert
                    </button>
                    <button
                      onClick={() => playTestSound('newToken')}
                      disabled={!soundEnabled || playingSound === 'newToken'}
                      className={`px-4 py-3 rounded-lg font-medium transition-all text-sm ${
                        playingSound === 'newToken'
                          ? 'bg-purple-200 animate-pulse'
                          : soundEnabled
                            ? 'bg-purple-50 hover:bg-purple-100 border border-purple-300'
                            : 'bg-gray-100 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      🪙 New Token
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Muted Chats - no milestone/recovery alerts sent to these groups or users */}
        {activeTab === 'mutedChats' && settings && (() => {
          const list = settings.mutedChatIds || [];
          const mutedGroups = list
            .map((entry, idx) => ({ ...(typeof entry === 'string' ? { chatId: entry, label: '', type: 'group' } : entry), idx }))
            .filter(e => (e.type || 'group') === 'group');
          const mutedUsers = list
            .map((entry, idx) => ({ ...(typeof entry === 'string' ? { chatId: entry, label: '', type: 'group' } : entry), idx }))
            .filter(e => e.type === 'user');
          const totalMuted = mutedGroups.length + mutedUsers.length;
          return (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-amber-200 dark:border-amber-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">🔇 Muted Chats</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Groups or users in this list will not receive any milestone or recovery alerts. Use this to stop alerts for specific chats without changing global settings.
              </p>
              {/* Summary: confirm which groups and users are muted */}
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Currently muted</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>{mutedGroups.length}</strong> group{mutedGroups.length !== 1 ? 's' : ''} muted
                  {mutedGroups.length > 0 && (
                    <span className="block mt-1 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {mutedGroups.map(e => e.label || e.chatId).join(', ')}
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                  <strong>{mutedUsers.length}</strong> user{mutedUsers.length !== 1 ? 's' : ''} muted
                  {mutedUsers.length > 0 && (
                    <span className="block mt-1 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {mutedUsers.map(e => e.label || e.chatId).join(', ')}
                    </span>
                  )}
                </p>
                {totalMuted === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No groups or users are muted.</p>
                )}
              </div>
              <div className="space-y-4">
                {/* Muted groups */}
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">👥 Muted groups</h3>
                  <ul className="space-y-2">
                    {mutedGroups.length === 0 ? (
                      <li className="text-sm text-gray-500 dark:text-gray-400 py-2">None</li>
                    ) : (
                      mutedGroups.map(({ chatId, label, idx }) => (
                        <li key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <span className="font-mono text-sm text-gray-800 dark:text-gray-200">{chatId}</span>
                          {label && <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">— {label}</span>}
                          <button
                            type="button"
                            onClick={() => updateSetting('mutedChatIds', (settings.mutedChatIds || []).filter((_, i) => i !== idx))}
                            className="ml-2 px-2 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            Remove
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                {/* Muted users */}
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">👤 Muted users</h3>
                  <ul className="space-y-2">
                    {mutedUsers.length === 0 ? (
                      <li className="text-sm text-gray-500 dark:text-gray-400 py-2">None</li>
                    ) : (
                      mutedUsers.map(({ chatId, label, idx }) => (
                        <li key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <span className="font-mono text-sm text-gray-800 dark:text-gray-200">{chatId}</span>
                          {label && <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">— {label}</span>}
                          <button
                            type="button"
                            onClick={() => updateSetting('mutedChatIds', (settings.mutedChatIds || []).filter((_, i) => i !== idx))}
                            className="ml-2 px-2 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            Remove
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Add muted chat</h3>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Chat ID (group or user)</label>
                      <input
                        type="text"
                        value={mutedChatIdInput}
                        onChange={(e) => setMutedChatIdInput(e.target.value)}
                        placeholder="-1001234567890 or 123456789"
                        className="w-48 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Label (optional)</label>
                      <input
                        type="text"
                        value={mutedChatLabelInput}
                        onChange={(e) => setMutedChatLabelInput(e.target.value)}
                        placeholder="e.g. Test group"
                        className="w-32 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Type</label>
                      <select
                        value={mutedChatTypeInput}
                        onChange={(e) => setMutedChatTypeInput(e.target.value)}
                        className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="group">Group</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const chatId = mutedChatIdInput.trim();
                        if (!chatId) return;
                        const list = settings.mutedChatIds || [];
                        if (list.some(e => (typeof e === 'string' ? e : e.chatId) === chatId)) return;
                        updateSetting('mutedChatIds', [...list, { chatId, label: mutedChatLabelInput.trim(), type: mutedChatTypeInput }]);
                        setMutedChatIdInput('');
                        setMutedChatLabelInput('');
                      }}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded"
                    >
                      Add
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Chat IDs are usually negative for groups (e.g. -1001234567890) and positive for users.</p>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Admin Group Settings */}
        {activeTab === 'adminGroup' && settings && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-2 border-purple-200">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">👑 Admin Group Settings</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Central configuration for admin alerts. All milestone, recovery, and ATL alerts are forwarded to this group with custom thresholds.
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Enable Admin Group</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Forward alerts to admin group</p>
                  </div>
                  <button
                    onClick={() => updateSetting('adminGroup.enabled', !settings.adminGroup?.enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.adminGroup?.enabled !== false ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                      settings.adminGroup?.enabled !== false ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Primary Admin Group Chat ID
                  </label>
                  <input
                    type="text"
                    value={settings.adminGroup?.chatId || '-4641106156'}
                    onChange={(e) => updateSetting('adminGroup.chatId', e.target.value)}
                    placeholder="-4641106156"
                    className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Main Telegram group where all admin alerts are sent
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Secondary Admin Group Chat ID
                  </label>
                  <input
                    type="text"
                    value={settings.adminGroup?.secondaryChatId || ''}
                    onChange={(e) => updateSetting('adminGroup.secondaryChatId', e.target.value.trim())}
                    placeholder="-1003662278156 (optional)"
                    className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional. A second group that receives the same admin alerts as the primary.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Additional Admin Group Chat IDs
                  </label>
                  <textarea
                    rows={2}
                    value={(settings.adminGroup?.extraChatIds || []).join(', ')}
                    onChange={(e) => {
                      const list = e.target.value
                        .split(/[\s,]+/)
                        .map((s) => s.trim())
                        .filter(Boolean);
                      updateSetting('adminGroup.extraChatIds', list);
                    }}
                    placeholder="-1009999999999, -1008888888888"
                    className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional. Comma- or space-separated chat IDs. Each gets the same admin alerts. The bot dedupes and skips the chat that originated an alert.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Admin Milestones (x multiplier)
                    </label>
                    <input
                      type="text"
                      value={(settings.adminGroup?.milestones || [1, 2, 3, 4, 5, 6, 10, 20]).join(', ')}
                      onChange={(e) => {
                        const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v) && v > 0);
                        updateSetting('adminGroup.milestones', values);
                      }}
                      placeholder="1, 2, 3, 4, 5, 6, 10, 20"
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Base milestones up to 100x. After 100x, uses 50x intervals.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Max Milestone Alert (x)
                    </label>
                    <select
                      value={settings.adminGroup?.maxMilestonePercent || 5000}
                      onChange={(e) => updateSetting('adminGroup.maxMilestonePercent', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value={100}>100x</option>
                      <option value={200}>200x</option>
                      <option value={500}>500x</option>
                      <option value={1000}>1000x</option>
                      <option value={2000}>2000x</option>
                      <option value={5000}>5000x</option>
                      <option value={0}>No Limit</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Stop milestone alerts after this multiplier
                    </p>
                  </div>
                </div>

                {/* Alert Forwarding Toggles */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Milestone Alerts</p>
                    </div>
                    <button
                      onClick={() => updateSetting('adminGroup.forwardMilestoneAlerts', !settings.adminGroup?.forwardMilestoneAlerts)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        settings.adminGroup?.forwardMilestoneAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                        settings.adminGroup?.forwardMilestoneAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Recovery Alerts</p>
                    </div>
                    <button
                      onClick={() => updateSetting('adminGroup.forwardRecoveryAlerts', !settings.adminGroup?.forwardRecoveryAlerts)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        settings.adminGroup?.forwardRecoveryAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                        settings.adminGroup?.forwardRecoveryAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Wallet Alerts</p>
                    </div>
                    <button
                      onClick={() => updateSetting('adminGroup.forwardWalletAlerts', !settings.adminGroup?.forwardWalletAlerts)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        settings.adminGroup?.forwardWalletAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                        settings.adminGroup?.forwardWalletAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">Bonded Alerts</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Launchpad graduation (pump.fun & others)</p>
                    </div>
                    <button
                      onClick={() => updateSetting('adminGroup.forwardBondedAlerts', !settings.adminGroup?.forwardBondedAlerts)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        settings.adminGroup?.forwardBondedAlerts !== false ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                        settings.adminGroup?.forwardBondedAlerts !== false ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* Cooldown Settings */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Milestone Cooldown</label>
                    <input
                      type="number"
                      value={settings.adminGroup?.milestoneCooldownMinutes || 30}
                      onChange={(e) => updateSetting('adminGroup.milestoneCooldownMinutes', parseInt(e.target.value))}
                      min="1"
                      max="1440"
                      className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">minutes</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Recovery Cooldown</label>
                    <input
                      type="number"
                      value={settings.adminGroup?.recoveryCooldownMinutes || 30}
                      onChange={(e) => updateSetting('adminGroup.recoveryCooldownMinutes', parseInt(e.target.value))}
                      min="1"
                      max="1440"
                      className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">minutes</p>
                  </div>
                </div>

                {/* Admin Recovery Settings */}
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-3">📈 Admin Recovery Settings</h4>
                  <p className="text-xs text-green-700 mb-4">
                    Separate recovery settings for admin group. These settings are independent from user/public group settings.
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Drawdown Threshold (%)
                      </label>
                      <input
                        type="number"
                        value={settings.adminGroup?.minDrawdownPercent || 70}
                        onChange={(e) => updateSetting('adminGroup.minDrawdownPercent', parseInt(e.target.value))}
                        min="10"
                        max="99"
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Min drop from ATH to start tracking recovery
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Min Recovery % to Alert
                      </label>
                      <input
                        type="number"
                        value={settings.adminGroup?.minRecoveryPercent || 50}
                        onChange={(e) => updateSetting('adminGroup.minRecoveryPercent', parseInt(e.target.value))}
                        min="1"
                        max="500"
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Only alert when recovery ≥ this %
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Recovery Milestones (%)
                      </label>
                      <input
                        type="text"
                        value={(settings.adminGroup?.recoveryMilestones || [100, 200, 300, 500, 1000]).join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v) && v > 0);
                          updateSetting('adminGroup.recoveryMilestones', values);
                        }}
                        placeholder="100, 200, 300, 500, 1000"
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Recovery % milestones to alert on
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Max Recovery Alert (%)
                      </label>
                      <select
                        value={settings.adminGroup?.maxRecoveryPercent || 1000}
                        onChange={(e) => updateSetting('adminGroup.maxRecoveryPercent', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      >
                        <option value={100}>100%</option>
                        <option value={200}>200%</option>
                        <option value={300}>300%</option>
                        <option value={500}>500%</option>
                        <option value={1000}>1000%</option>
                        <option value={2000}>2000%</option>
                        <option value={5000}>5000%</option>
                        <option value={0}>No Limit</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Stop recovery alerts after this %
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Group Info Box */}
              <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-2">Admin Group Features</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Receives ALL alerts from all tracked tokens across all users/groups</li>
                  <li>• Uses lower milestone thresholds (1x, 2x, 3x...) for early alerts</li>
                  <li>• After 100x, milestones switch to 50x intervals (150x, 200x, 250x...)</li>
                  <li>• Max milestone setting limits how high alerts go (default 5000x)</li>
                  <li>• Regular users/groups use default milestones starting at 5x</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Alert Templates */}
        {activeTab === 'templates' && settings && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">📝 Alert Message Templates</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Customize the format of alert messages. Use tags like #name, #symbol, #milestone, #imc, #mc, #growth, #address, #chain
              </p>

              <div className="space-y-6">
                {/* Milestone Alert Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    🎯 Milestone Alert Template
                  </label>
                  <textarea
                    value={settings.alertTemplates?.milestone || '*🎯 Milestone Alert*\n\n#chainemoji *#name #milestonex Cooked*\n\n💰 Initial MC: #imc\n💵 Current MC: #mc\n📈 Growth: #growth\n\n`#address`'}
                    onChange={(e) => updateSetting('alertTemplates.milestone', e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="*🎯 Milestone Alert*..."
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Tags: #name, #symbol, #milestone, #imc, #mc, #growth, #address, #chainemoji
                  </p>
                </div>

                {/* Bottom Recovery Detected Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    📈 Bottom Recovery Detected Template
                  </label>
                  <textarea
                    value={settings.alertTemplates?.recoveryDetected || '*📈 Bottom Recovery Detected*\n\n#chainemoji #name (#symbol)\n\n📉 Bottom: #bottom\n💵 Current MC: #mc\n📈 Recovery: #recovery\n📊 From ATH: #fromath\n\n`#address`'}
                    onChange={(e) => updateSetting('alertTemplates.recoveryDetected', e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="*📈 Bottom Recovery Detected*..."
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Tags: #name, #symbol, #bottom, #mc, #recovery, #fromath, #address, #chainemoji
                  </p>
                </div>

                {/* Bottom Recovery Alert Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    🎯 Bottom Recovery Alert Template
                  </label>
                  <textarea
                    value={settings.alertTemplates?.recoveryMilestone || '*🎯 Bottom Recovery Alert*\n\n#chainemoji #name (#symbol)\n🎯 *#milestone% Recovery From Bottom*\n\n📉 Bottom: #bottom\n💵 Current MC: #mc\n📈 Recovery: #recovery\n📊 From ATH: #fromath\n\n`#address`'}
                    onChange={(e) => updateSetting('alertTemplates.recoveryMilestone', e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="*🎯 Bottom Recovery Alert*..."
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Tags: #name, #symbol, #milestone, #bottom, #mc, #recovery, #fromath, #address, #chainemoji
                  </p>
                </div>

                {/* Template Info */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Template Tags Reference</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-blue-800 dark:text-blue-300">
                    <div><code>#name</code> - Token name</div>
                    <div><code>#symbol</code> - Token symbol</div>
                    <div><code>#milestone</code> - Milestone value (e.g., 5)</div>
                    <div><code>#imc</code> - Initial market cap</div>
                    <div><code>#mc</code> - Current market cap</div>
                    <div><code>#growth</code> - Growth percentage</div>
                    <div><code>#address</code> - Contract address</div>
                    <div><code>#chainemoji</code> - Chain emoji</div>
                    <div><code>#bottom</code> - Bottom market cap</div>
                    <div><code>#recovery</code> - Recovery percentage</div>
                    <div><code>#fromath</code> - Drop from ATH</div>
                    <div><code>#sharedby</code> - Who shared the token</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Cleanup Section */}
        {activeTab === 'cleanup' && settings && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🧹 Data Cleanup</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Remove invalid milestone entries that have incorrect data (e.g., no actual price gain).
              </p>

              <div className="space-y-4">
                {/* Invalid Milestone Cleanup */}
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">Invalid Milestone Cleanup</h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    This will remove milestone entries where the latest market cap is less than or equal to the initial market cap (no real gain achieved).
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.post('/analytics/milestones/cleanup', { dryRun: true });
                          const data = response.data;
                          if (data.invalidEntries && data.invalidEntries.length > 0) {
                            alert(`Found ${data.invalidEntries.length} invalid entries:\n\n${data.invalidEntries.map(e => `${e.symbol} (${e.chain}): Initial $${e.initialMC?.toLocaleString()} / Latest $${e.latestMC?.toLocaleString()}`).join('\n')}`);
                          } else {
                            alert('No invalid milestone entries found!');
                          }
                        } catch (err) {
                          console.error('Cleanup preview error:', err);
                          alert('Failed to preview cleanup: ' + (err.response?.data?.error || err.message));
                        }
                      }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      Preview Cleanup
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete all invalid milestone entries? This cannot be undone.')) {
                          return;
                        }
                        try {
                          const response = await api.post('/analytics/milestones/cleanup', { dryRun: false });
                          const data = response.data;
                          alert(`Successfully removed ${data.deletedCount} invalid milestone entries.`);
                        } catch (err) {
                          console.error('Cleanup error:', err);
                          alert('Failed to cleanup: ' + (err.response?.data?.error || err.message));
                        }
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Run Cleanup
                    </button>
                  </div>
                </div>

                {/* Clear All Milestones */}
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Clear All Milestones</h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                    Permanently delete ALL milestone entries from the database. This action cannot be undone.
                  </p>
                  <button
                    onClick={async () => {
                      if (!window.confirm('WARNING: This will permanently delete ALL milestone entries. Are you absolutely sure?')) {
                        return;
                      }
                      if (!window.confirm('This is your last chance to cancel. Click OK to proceed with deletion.')) {
                        return;
                      }
                      try {
                        const response = await api.delete('/analytics/milestones/all');
                        alert(`Successfully cleared ${response.data.deletedCount} milestone entries.`);
                      } catch (err) {
                        console.error('Clear milestones error:', err);
                        alert('Failed to clear milestones: ' + (err.response?.data?.error || err.message));
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Clear All Milestones
                  </button>
                </div>

                {/* Clear All Recovery Entries */}
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Clear All Recovery Entries</h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                    Permanently delete ALL recovery tracking entries from the database. This action cannot be undone.
                  </p>
                  <button
                    onClick={async () => {
                      if (!window.confirm('WARNING: This will permanently delete ALL recovery entries. Are you absolutely sure?')) {
                        return;
                      }
                      if (!window.confirm('This is your last chance to cancel. Click OK to proceed with deletion.')) {
                        return;
                      }
                      try {
                        const response = await api.delete('/analytics/recovery/all');
                        alert(`Successfully cleared ${response.data.deletedCount} recovery entries.`);
                      } catch (err) {
                        console.error('Clear recovery error:', err);
                        alert('Failed to clear recovery entries: ' + (err.response?.data?.error || err.message));
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Clear All Recovery Entries
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Membership Tiers */}
        {activeTab === 'tiers' && settings && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure Starter limits for private users and groups. Premium is unlimited (monthly subscription with start/expiry dates).
              Set the monthly price in USD — admins activate Premium from Users/Groups. Alpha is free and assigned manually — not configured here.
            </p>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">👤 Private user tiers</h2>
              <div className="space-y-6">
                {MEMBERSHIP_TIER_UI.map((tier) => (
                  <div key={tier.id} className={`p-4 rounded-lg ${tier.cardClass}`}>
                    <h3 className="font-semibold text-lg mb-3">
                      {tier.emoji} {tier.label}
                      {tier.id === 'premium' && (
                        <span className="ml-2 text-xs font-normal text-gray-500">— unlimited at 0 · monthly subscription</span>
                      )}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {!tier.hideLimits && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Max Tokens</label>
                            <input
                              type="number"
                              value={tierField(settings.userTiers, tier.id, 'maxTokens', 0)}
                              onChange={(e) => updateSetting(`userTiers.${tier.id}.maxTokens`, parseInt(e.target.value, 10))}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Min Interval</label>
                            <input
                              type="text"
                              value={tierField(settings.userTiers, tier.id, 'minInterval', '')}
                              onChange={(e) => updateSetting(`userTiers.${tier.id}.minInterval`, e.target.value)}
                              placeholder="15m, 1h, etc"
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Max Groups</label>
                            <input
                              type="number"
                              value={tierField(settings.userTiers, tier.id, 'maxGroups', 0)}
                              onChange={(e) => updateSetting(`userTiers.${tier.id}.maxGroups`, parseInt(e.target.value, 10))}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                      )}
                      {tier.hideLimits && (
                        <div className="md:col-span-3 text-sm text-gray-600 dark:text-gray-400">
                          Unlimited tokens, groups, and 1-minute intervals while subscription is active. Auto-downgrades to Starter after expiry.
                        </div>
                      )}
                      {tier.showPrice && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Price ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={tierField(settings.userTiers, tier.id, 'price', 0)}
                            onChange={(e) => updateSetting(`userTiers.${tier.id}.price`, parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">👥 Group tiers</h2>
              <div className="space-y-6">
                {MEMBERSHIP_TIER_UI.map((tier) => (
                  <div key={tier.id} className={`p-4 rounded-lg ${tier.cardClass}`}>
                    <h3 className="font-semibold text-lg mb-3">
                      {tier.emoji} {tier.label}
                      {tier.id === 'premium' && (
                        <span className="ml-2 text-xs font-normal text-gray-500">— unlimited at 0 · monthly subscription</span>
                      )}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {!tier.hideLimits && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Max Tokens</label>
                            <input
                              type="number"
                              value={tierField(settings.groupTiers, tier.id, 'maxTokens', 0)}
                              onChange={(e) => updateSetting(`groupTiers.${tier.id}.maxTokens`, parseInt(e.target.value, 10))}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Min Interval</label>
                            <input
                              type="text"
                              value={tierField(settings.groupTiers, tier.id, 'minInterval', '')}
                              onChange={(e) => updateSetting(`groupTiers.${tier.id}.minInterval`, e.target.value)}
                              placeholder="15m, 1h, etc"
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Max Members (0=unlimited)</label>
                            <input
                              type="number"
                              value={tierField(settings.groupTiers, tier.id, 'maxMembers', 0)}
                              onChange={(e) => updateSetting(`groupTiers.${tier.id}.maxMembers`, parseInt(e.target.value, 10))}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                      )}
                      {tier.hideLimits && (
                        <div className="md:col-span-3 text-sm text-gray-600 dark:text-gray-400">
                          Unlimited tokens, members, and 1-minute intervals while subscription is active. Auto-downgrades to Starter after expiry.
                        </div>
                      )}
                      {tier.showPrice && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Price ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={tierField(settings.groupTiers, tier.id, 'price', 0)}
                            onChange={(e) => updateSetting(`groupTiers.${tier.id}.price`, parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Wallet Monitor Settings */}
        {activeTab === 'walletMonitor' && settings && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">👛 Wallet Monitor Configuration</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Check Interval (Minutes)</label>
                  <input
                    type="number"
                    value={settings.walletMonitor?.checkIntervalMinutes || 2}
                    onChange={(e) => updateSetting('walletMonitor.checkIntervalMinutes', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">How often to check wallets for new transactions</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Alert Destination (Chat ID)</label>
                  <input
                    type="text"
                    value={settings.walletMonitor?.alertChatId || ''}
                    onChange={(e) => updateSetting('walletMonitor.alertChatId', e.target.value)}
                    placeholder="-1234567890"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Chat ID where wallet-detected tokens are tracked and alerts sent. Tokens are tagged as "System (Wallet Monitor)".</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Default Min Market Cap ($)</label>
                  <input
                    type="number"
                    value={settings.walletMonitor?.defaultMinMarketCap || 10000}
                    onChange={(e) => updateSetting('walletMonitor.defaultMinMarketCap', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Default Max Market Cap ($)</label>
                  <input
                    type="number"
                    value={settings.walletMonitor?.defaultMaxMarketCap || 500000}
                    onChange={(e) => updateSetting('walletMonitor.defaultMaxMarketCap', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feature Flags */}
        {activeTab === 'features' && settings && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🚩 Feature Flags</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Enable or disable system features.</p>
              <div className="space-y-4">
                {[
                  { key: 'userAccounts', label: 'User Accounts', desc: 'Enable individual user account tracking' },
                  { key: 'groupAccounts', label: 'Group Accounts', desc: 'Enable group/channel account tracking' },
                  { key: 'membershipTiers', label: 'Membership Tiers', desc: 'Enable tiered membership system' },
                  { key: 'referralSystem', label: 'Referral System', desc: 'Enable referral rewards' },
                  { key: 'trialPeriods', label: 'Trial Periods', desc: 'Enable free trials for premium features' },
                  { key: 'analytics', label: 'Analytics', desc: 'Enable detailed analytics tracking' },
                ].map((feature) => (
                  <div key={feature.key} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{feature.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{feature.desc}</p>
                    </div>
                    <button
                      onClick={() => updateSetting(`features.${feature.key}`, !settings.features?.[feature.key])}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.features?.[feature.key] ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                        settings.features?.[feature.key] ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Security */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Two-Factor Authentication */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">🔐 Two-Factor Authentication</h2>
                {twoFactorEnabled && (
                  <span className="text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded font-medium">✓ Enabled</span>
                )}
              </div>

              {twoFactorSuccess && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <p className="text-green-800 text-sm">{twoFactorSuccess}</p>
                </div>
              )}

              {twoFactorError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-red-800 text-sm">{twoFactorError}</p>
                </div>
              )}

              {twoFactorStep === 'idle' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {twoFactorEnabled
                      ? 'Two-factor authentication is enabled. You need to enter a code when logging in.'
                      : 'Add an extra layer of security by requiring a code from your authenticator app.'}
                  </p>
                  {!twoFactorEnabled ? (
                    <button
                      onClick={start2FASetup}
                      disabled={twoFactorLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {twoFactorLoading ? 'Setting up...' : 'Enable 2FA'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setTwoFactorStep('disable')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Disable 2FA
                    </button>
                  )}
                </div>
              )}

              {twoFactorStep === 'setup' && twoFactorSetup && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">Step 1: Scan QR Code</h3>
                    <div className="flex justify-center mb-4">
                      <img src={twoFactorSetup.qrCode} alt="2FA QR Code" className="border-4 border-white shadow-lg rounded-lg" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-blue-700 mb-1">Or enter manually:</p>
                      <code className="text-sm bg-white dark:bg-gray-800 px-3 py-1 rounded border font-mono">{twoFactorSetup.secret}</code>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Step 2: Verify Code</h3>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        className="flex-1 px-4 py-2 border rounded-lg text-center text-xl tracking-widest font-mono"
                      />
                      <button
                        onClick={verify2FA}
                        disabled={twoFactorLoading || verifyCode.length !== 6}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {twoFactorLoading ? 'Verifying...' : 'Verify'}
                      </button>
                    </div>
                  </div>
                  <button onClick={cancel2FAAction} className="w-full px-4 py-2 border text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800/50">
                    Cancel
                  </button>
                </div>
              )}

              {twoFactorStep === 'disable' && (
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <h3 className="font-semibold text-red-900 mb-4">Disable Two-Factor Authentication</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Password</label>
                        <input
                          type="password"
                          value={disablePassword}
                          onChange={(e) => setDisablePassword(e.target.value)}
                          className="w-full px-4 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">2FA Code</label>
                        <input
                          type="text"
                          value={disableCode}
                          onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          className="w-full px-4 py-2 border rounded-lg text-center text-xl tracking-widest font-mono"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={cancel2FAAction} className="flex-1 px-4 py-2 border text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800/50">
                      Cancel
                    </button>
                    <button
                      onClick={disable2FA}
                      disabled={twoFactorLoading}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {twoFactorLoading ? 'Disabling...' : 'Disable 2FA'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* IP Whitelist Management */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">🌐 IP Whitelist</h2>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  Your IP: <code className="font-mono">{currentIP}</code>
                </span>
              </div>

              {/* IP Whitelist Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Enable IP Whitelist</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">When disabled, all IPs can access the dashboard</p>
                </div>
                <button
                  onClick={() => updateSetting('security.ipWhitelistEnabled', !settings?.security?.ipWhitelistEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.security?.ipWhitelistEnabled !== false ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                    settings?.security?.ipWhitelistEnabled !== false ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Control which IP addresses can access the admin dashboard. When enabled, only whitelisted IPs will be allowed.
              </p>

              {ipSuccess && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <p className="text-green-800 text-sm">{ipSuccess}</p>
                </div>
              )}

              {ipError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-red-800 text-sm">{ipError}</p>
                </div>
              )}

              {/* Add New IP */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">Add IP Address</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newIPAddress}
                      onChange={(e) => setNewIPAddress(e.target.value)}
                      placeholder="IP address (e.g., 192.168.1.1)"
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newIPLabel}
                      onChange={(e) => setNewIPLabel(e.target.value)}
                      placeholder="Label (optional)"
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={addCurrentIP}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 whitespace-nowrap"
                      title="Use your current IP"
                    >
                      Use My IP
                    </button>
                    <button
                      onClick={addIPToWhitelist}
                      disabled={ipLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      {ipLoading ? 'Adding...' : 'Add IP'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Use * to allow all IPs (not recommended for production)
                </p>
              </div>

              {/* IP List */}
              <div className="space-y-2">
                {ipWhitelist.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No IPs in whitelist. All IPs are currently allowed.</p>
                  </div>
                ) : (
                  ipWhitelist.map((ip, index) => (
                    <div
                      key={ip.id || index}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        ip.isSystem ? 'bg-blue-50 border border-blue-200 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800/50'
                      } ${ip.enabled === false ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <code className="font-mono text-sm bg-white dark:bg-gray-800 px-2 py-1 rounded border">
                          {ip.address}
                        </code>
                        {ip.label && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">{ip.label}</span>
                        )}
                        {ip.isSystem && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            Environment
                          </span>
                        )}
                        {ip.address === currentIP && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!ip.isSystem && (
                          <>
                            <button
                              onClick={() => toggleIPEnabled(ip.id, ip.enabled)}
                              className={`px-3 py-1 text-xs rounded ${
                                ip.enabled !== false
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-200 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {ip.enabled !== false ? 'Enabled' : 'Disabled'}
                            </button>
                            <button
                              onClick={() => removeIPFromWhitelist(ip.id)}
                              className="px-3 py-1 text-xs bg-red-100 text-red-700 dark:text-red-400 rounded hover:bg-red-200"
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {ipWhitelist.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    <strong>Note:</strong> IP whitelist is only enforced in production environment.
                    Environment-defined IPs (from ALLOWED_IPS) cannot be removed from the dashboard.
                  </p>
                </div>
              )}
            </div>

            {/* System Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">ℹ️ System Information</h2>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Bot Version</span>
                  <span className="font-medium">v1.3.0</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Wallet System</span>
                  <span className="font-medium">HD Wallet with One-Time Addresses</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Last Settings Update</span>
                  <span className="font-medium">
                    {settings?.updatedAt ? new Date(settings.updatedAt).toLocaleString() : 'Never'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Updated By</span>
                  <span className="font-medium">{settings?.updatedBy || 'System'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Monitor / Processor Tab */}
        {activeTab === 'processor' && (
          <div className="space-y-6">
            {/* Header with Refresh */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">🖥️ System Monitor</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">View running processes, daemons, and automation systems</p>
              </div>
              <button
                onClick={fetchSystemProcesses}
                disabled={systemLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {systemLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>🔄 Refresh</>
                )}
              </button>
            </div>

            {systemError && (
              <div className="bg-red-50 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                {systemError}
              </div>
            )}

            {/* API Health Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">API Endpoints Health</h3>
                <button
                  onClick={fetchApiHealth}
                  disabled={apiHealthLoading}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {apiHealthLoading ? 'Checking...' : 'Check Health'}
                </button>
              </div>

              {!apiHealth && !apiHealthLoading && (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">Click "Check Health" to test API endpoints</p>
              )}

              {apiHealth && (
                <div>
                  {/* Overall Status */}
                  <div className={`flex items-center gap-3 mb-4 p-3 rounded-lg ${
                    apiHealth.overall === 'healthy' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                    apiHealth.overall === 'partial' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
                    'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  }`}>
                    <span className={`text-2xl`}>
                      {apiHealth.overall === 'healthy' ? '🟢' : apiHealth.overall === 'partial' ? '🟡' : '🔴'}
                    </span>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white capitalize">{apiHealth.overall}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {apiHealth.healthy}/{apiHealth.total} endpoints healthy
                        {apiHealth.criticalDown > 0 && ` • ${apiHealth.criticalDown} critical down`}
                        {apiHealth.checkedAt && ` • Checked: ${new Date(apiHealth.checkedAt).toLocaleTimeString()}`}
                      </p>
                    </div>
                  </div>

                  {/* Endpoints Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {apiHealth.endpoints.map((ep, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2.5 rounded-lg border ${
                          ep.status === 'healthy'
                            ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
                            : 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ep.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {ep.name}
                              {ep.critical && <span className="ml-1 text-[10px] text-orange-600 dark:text-orange-400 font-bold">CRITICAL</span>}
                            </p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate">{ep.path}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className={`text-xs font-semibold ${ep.status === 'healthy' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {ep.httpStatus || 'ERR'}
                          </p>
                          <p className="text-[10px] text-gray-400">{ep.responseTime}ms</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!systemProcesses && !systemLoading && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">🖥️</div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">No Data Loaded</h3>
                <p className="text-gray-500 mb-4">Click the Refresh button to load system process information</p>
                <button
                  onClick={fetchSystemProcesses}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Load System Data
                </button>
              </div>
            )}

            {systemProcesses && (
              <>
                {/* System Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-2">⏱️ System Uptime</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">{systemProcesses.uptime || 'N/A'}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-2">💾 Memory</h3>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap overflow-x-auto">{systemProcesses.memory || 'N/A'}</pre>
                  </div>
                </div>

                {/* PM2 Processes */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">🚀 PM2 Processes</h3>
                  {systemProcesses.pm2?.error ? (
                    <p className="text-yellow-600">{systemProcesses.pm2.error}</p>
                  ) : systemProcesses.pm2?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Uptime</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Restarts</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CPU</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Memory</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {systemProcesses.pm2.map((proc, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:bg-gray-800/50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{proc.name}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  proc.status === 'online' ? 'bg-green-100 text-green-800 dark:text-green-300' :
                                  proc.status === 'stopped' ? 'bg-gray-100 text-gray-800 dark:text-gray-100' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {proc.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-mono">{proc.pid || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatUptime(proc.uptime)}</td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{proc.restarts || 0}</td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{proc.cpu ? `${proc.cpu}%` : '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatBytes(proc.memory)}</td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex gap-1 flex-wrap">
                                  <button
                                    onClick={() => pm2Action('restart', proc.name)}
                                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                    title="Restart"
                                  >
                                    🔄
                                  </button>
                                  {proc.status === 'online' ? (
                                    <button
                                      onClick={() => pm2Action('stop', proc.name)}
                                      className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                      title="Stop"
                                    >
                                      ⏹️
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => pm2Action('start', proc.name)}
                                      className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                      title="Start"
                                    >
                                      ▶️
                                    </button>
                                  )}
                                  <button
                                    onClick={() => fetchProcessLogs(proc.name, 'out')}
                                    className="px-2 py-1 text-xs bg-gray-50 dark:bg-gray-800/500 text-white rounded hover:bg-gray-600"
                                    title="View Logs"
                                  >
                                    📋
                                  </button>
                                  <button
                                    onClick={() => fetchProcessLogs(proc.name, 'err')}
                                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                    title="View Error Logs"
                                  >
                                    ⚠️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500">No PM2 processes found</p>
                  )}
                </div>

                {/* Process Logs Modal */}
                {selectedProcessLogs && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        📋 Logs: {selectedProcessLogs.process} ({logsType === 'err' ? 'Errors' : 'Output'})
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchProcessLogs(selectedProcessLogs.process, 'out')}
                          className={`px-3 py-1 text-sm rounded ${logsType === 'out' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 dark:text-gray-300'}`}
                        >
                          Output
                        </button>
                        <button
                          onClick={() => fetchProcessLogs(selectedProcessLogs.process, 'err')}
                          className={`px-3 py-1 text-sm rounded ${logsType === 'err' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 dark:text-gray-300'}`}
                        >
                          Errors
                        </button>
                        <button
                          onClick={() => setSelectedProcessLogs(null)}
                          className="px-3 py-1 text-sm bg-gray-300 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-auto">
                      <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                        {logsLoading ? 'Loading logs...' : selectedProcessLogs.logs || 'No logs available'}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Cron Jobs - Full Management */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">⏰ Cron Jobs</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={fetchCronLog}
                        className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                      >
                        {cronLogLoading ? 'Loading...' : '📄 View Log'}
                      </button>
                      <button
                        onClick={() => { fetchCronJobs(); }}
                        className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                      >
                        🔄 Refresh
                      </button>
                      <button
                        onClick={openAddCron}
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        + Add Cron Job
                      </button>
                    </div>
                  </div>

                  {/* Cron Form (Add / Edit) */}
                  {showCronForm && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-gray-700/50 rounded-lg border border-blue-200 dark:border-gray-600">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        {editingCron !== null ? '✏️ Edit Cron Job' : '➕ New Cron Job'}
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Schedule (cron expression)</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={cronForm.schedule}
                              onChange={(e) => setCronForm({ ...cronForm, schedule: e.target.value })}
                              placeholder="0 3 * * *"
                              className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                            />
                            <select
                              onChange={(e) => { if (e.target.value) setCronForm({ ...cronForm, schedule: e.target.value }); }}
                              className="px-2 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                              value=""
                            >
                              <option value="">Presets...</option>
                              {CRON_PRESETS.map((p, i) => (
                                <option key={i} value={p.value}>{p.label} ({p.value})</option>
                              ))}
                            </select>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">Format: minute hour day-of-month month day-of-week</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Command</label>
                          <input
                            type="text"
                            value={cronForm.command}
                            onChange={(e) => setCronForm({ ...cronForm, command: e.target.value })}
                            placeholder="cd /root/CKB && node cleanup-dead-tokens.js >> /root/CKB/logs/cleanup.log 2>&1"
                            className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setShowCronForm(false); setEditingCron(null); }}
                            className="px-3 py-1.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveCronJob}
                            className="px-4 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          >
                            {editingCron !== null ? 'Update' : 'Add'} Cron Job
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cron Jobs Table */}
                  {cronJobs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Schedule</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Command</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {cronJobs.map((job) => (
                            <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                                {job.name || `Job ${job.id + 1}`}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-blue-600 dark:text-blue-400">
                                {job.schedule || 'Custom'}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400 max-w-xs truncate" title={job.command}>
                                {job.command}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex gap-1 justify-end">
                                  <button
                                    onClick={() => runCronJob(job.id)}
                                    disabled={cronRunning !== null}
                                    className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition disabled:opacity-50"
                                    title="Run now"
                                  >
                                    {cronRunning === job.id ? '⏳ Running...' : '▶ Run'}
                                  </button>
                                  <button
                                    onClick={() => openEditCron(job)}
                                    className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition"
                                    title="Edit"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => deleteCronJob(job.id)}
                                    className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition"
                                    title="Delete"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : systemProcesses.cron?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Schedule</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Command</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {systemProcesses.cron.map((job, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-4 py-3 text-sm font-mono text-blue-600">{job.schedule || 'Custom'}</td>
                              <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400 max-w-md truncate" title={job.command || job.raw}>
                                {job.command || job.raw}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button onClick={fetchCronJobs} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                                  Load to manage
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No cron jobs configured. Click "Add Cron Job" to create one.</p>
                  )}

                  {/* Run Output */}
                  {cronOutput && (
                    <div className={`mt-4 p-4 rounded-lg ${cronOutput.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className={`text-sm font-bold ${cronOutput.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                          {cronOutput.success ? '✅ Execution Successful' : '❌ Execution Failed'}
                        </h4>
                        <button onClick={() => setCronOutput(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
                      </div>
                      {cronOutput.error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{cronOutput.error}</p>}
                      {cronOutput.output && (
                        <div className="bg-gray-900 rounded p-3 max-h-60 overflow-auto">
                          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{cronOutput.output}</pre>
                        </div>
                      )}
                      {cronOutput.errors && (
                        <div className="bg-gray-900 rounded p-3 max-h-40 overflow-auto mt-2">
                          <pre className="text-xs text-yellow-400 font-mono whitespace-pre-wrap">{cronOutput.errors}</pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cron Log Viewer */}
                  {cronLog !== null && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">📄 Cleanup Log</h4>
                        <div className="flex gap-2">
                          <button onClick={fetchCronLog} className="text-xs text-blue-600 hover:text-blue-700">🔄 Refresh</button>
                          <button onClick={() => setCronLog(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
                        </div>
                      </div>
                      <div className="bg-gray-900 rounded p-3 max-h-72 overflow-auto">
                        <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{cronLog}</pre>
                      </div>
                    </div>
                  )}
                </div>

                {/* Node Processes */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">📦 Node.js Processes</h3>
                  {systemProcesses.nodeProcesses?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CPU %</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Mem %</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Command</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {systemProcesses.nodeProcesses.map((proc, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:bg-gray-800/50">
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{proc.user}</td>
                              <td className="px-4 py-3 text-sm font-mono text-gray-500 dark:text-gray-400">{proc.pid}</td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{proc.cpu}%</td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{proc.memory}%</td>
                              <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400 max-w-xs truncate" title={proc.command}>
                                {proc.command}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500">No Node.js processes found</p>
                  )}
                </div>

                {/* Systemd Services */}
                {systemProcesses.systemd?.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">🔧 Systemd Services</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Service</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">State</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {systemProcesses.systemd.map((svc, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:bg-gray-800/50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{svc.unit}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  svc.active === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800 dark:text-gray-100'
                                }`}>
                                  {svc.sub}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{svc.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Git Commit History Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">📦 Version & Commit History</h3>
                <button
                  onClick={fetchGitInfo}
                  disabled={gitLoading}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                >
                  {gitLoading ? 'Loading...' : '🔄 Load Git Info'}
                </button>
              </div>

              {!gitInfo && !gitLoading && (
                <p className="text-gray-500 text-center py-4">Click "Load Git Info" to view commit history</p>
              )}

              {gitInfo && (
                <div className="space-y-6">
                  {/* Bot Repository */}
                  {gitInfo.bot && !gitInfo.bot.error && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">🤖 {gitInfo.bot.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{gitInfo.bot.path}</p>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:text-blue-300 text-xs rounded-full font-medium">
                            {gitInfo.bot.branch}
                          </span>
                          {gitInfo.bot.uncommittedChanges > 0 && (
                            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 dark:text-yellow-300 text-xs rounded-full font-medium">
                              {gitInfo.bot.uncommittedChanges} uncommitted
                            </span>
                          )}
                        </div>
                      </div>

                      {gitInfo.bot.lastCommit && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-3 mb-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Latest Commit</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{gitInfo.bot.lastCommit.message}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span className="font-mono text-blue-600">{gitInfo.bot.lastCommit.hash?.substring(0, 7)}</span>
                            {' • '}{gitInfo.bot.lastCommit.author}
                            {' • '}{gitInfo.bot.lastCommit.date ? new Date(gitInfo.bot.lastCommit.date).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      )}

                      {gitInfo.bot.recentCommits?.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Recent Commits</p>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {gitInfo.bot.recentCommits.map((commit, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                                <span className="font-mono text-blue-600 flex-shrink-0">{commit.hash}</span>
                                <span className="text-gray-700 flex-1 truncate" title={commit.message}>{commit.message}</span>
                                <span className="text-gray-400 flex-shrink-0">{commit.time}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {gitInfo.bot?.error && (
                    <div className="border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                      <p className="text-yellow-700 text-sm">🤖 Bot: {gitInfo.bot.error}</p>
                    </div>
                  )}

                  {/* Dashboard Repository */}
                  {gitInfo.dashboard && !gitInfo.dashboard.error && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">🖥️ {gitInfo.dashboard.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{gitInfo.dashboard.path}</p>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:text-blue-300 text-xs rounded-full font-medium">
                            {gitInfo.dashboard.branch}
                          </span>
                          {gitInfo.dashboard.uncommittedChanges > 0 && (
                            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 dark:text-yellow-300 text-xs rounded-full font-medium">
                              {gitInfo.dashboard.uncommittedChanges} uncommitted
                            </span>
                          )}
                        </div>
                      </div>

                      {gitInfo.dashboard.lastCommit && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-3 mb-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Latest Commit</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{gitInfo.dashboard.lastCommit.message}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span className="font-mono text-blue-600">{gitInfo.dashboard.lastCommit.hash?.substring(0, 7)}</span>
                            {' • '}{gitInfo.dashboard.lastCommit.author}
                            {' • '}{gitInfo.dashboard.lastCommit.date ? new Date(gitInfo.dashboard.lastCommit.date).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      )}

                      {gitInfo.dashboard.recentCommits?.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Recent Commits</p>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {gitInfo.dashboard.recentCommits.map((commit, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                                <span className="font-mono text-blue-600 flex-shrink-0">{commit.hash}</span>
                                <span className="text-gray-700 flex-1 truncate" title={commit.message}>{commit.message}</span>
                                <span className="text-gray-400 flex-shrink-0">{commit.time}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {gitInfo.dashboard?.error && (
                    <div className="border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                      <p className="text-yellow-700 text-sm">🖥️ Dashboard: {gitInfo.dashboard.error}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
